import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { dropOnNode, t, toggleExpanded, visibleRows, type FillInk, type TreeNode } from '@bms2/shared';
import { Icon } from '../Icon';
import { FILL_INK_TOKEN, fillInkFor } from './treeFill';
import styles from './shared.module.css';

/**
 * Nested Tree List (piece 4): one expand/collapse tree over a self-referencing
 * node shape, built once for Term Container, Asset Manager and Knowledge Base
 * categories. A row collapses to itself plus a child count and expands in
 * place, with no navigation and no modal; an optional coloured left bar
 * follows the Level Symbols convention, or fills the whole row where the
 * host asks (decision 1290), drawn then as a stack of cards whose widths step
 * inward with depth (decision 1420); a leaf expands to its own inline preview
 * independent of its parent.
 */
export const treeListDocumentedStates = ['collapsed', 'expanded', 'leafPreview', 'levels'] as const;

export interface TreeListProps<N extends TreeNode> {
  nodes: readonly N[];
  label: string;
  initiallyExpanded?: readonly string[];
  /** A leaf's own content, shown inline when the leaf row is expanded. */
  renderPreview?: (node: N) => ReactNode;
  /** A screen that selects a row, such as the category tree with its panel beside it: the label becomes the control, and the selected row says so. */
  onSelect?: (node: N) => void;
  selectedId?: string | null;
  /** Facts the row carries beside its label, such as the org chart's title and department. */
  renderMeta?: (node: N) => ReactNode;
  /** The row's own controls at its right, such as the org chart's Move Person. */
  renderActions?: (node: N) => ReactNode;
  /**
   * A tree whose parent is the fact being edited takes a row dropped onto
   * another row (piece 10, decision 0079). The drop onto a row itself or onto
   * one of its own descendants never reaches the host. 🔨 The pointer drag is
   * an affordance and never the only path: a host that offers it also offers
   * the same move as a row action, which is what a keyboard reaches.
   */
  onDropOn?: (dragged: N, target: N) => void;
  /**
   * Content a host draws beneath a row whatever the row is, open or closed, leaf
   * or parent: the terms library's add form opened from a section's own +
   * (decision 0771). A row answering nothing draws nothing.
   */
  renderAfter?: (node: N) => ReactNode;
  /**
   * 🔒 A host whose rows are levels fills each row with its level colour across
   * the row's full width (decision 1290), and the row's text takes the light or
   * the dark text token by the fill's contrast. The asset tree asks for it; the
   * terms library and the category tree keep the edge bar. A row with no colour
   * draws unfilled either way.
   *
   * 🔒 A filled tree draws as a stack of cards (decision 1420): a small radius
   * and a hairline of the page between rows, a deeper step per level, and a rail
   * in each ancestor's colour down the gutter beside everything it holds. The
   * facts stand at the row's right in the caption size, the selected row takes an
   * accent ring outside a ring of the page, and hover never repaints the fill.
   */
  fillRows?: boolean;
  /**
   * A small square drawn at the row's left, after the expand control and before
   * the label, such as an asset's picture (decision 1420). A row answering
   * nothing draws nothing there.
   */
  renderLead?: (node: N) => ReactNode;
  /**
   * A row that heads a section rather than standing as a node, such as the asset
   * tree's site (decision 1420): a plain heading with a rule beneath it and room
   * above, never filled and never counted, since the host names its own count.
   */
  isHeading?: (node: N) => boolean;
}

/**
 * 🔒 The rails of a filled tree (decision 1420): one thin line per ancestor, in
 * that ancestor's colour or the neutral rail's where it has none, standing in the
 * gutter under the ancestor's expand control. Each row draws the rails of every
 * ancestor above it, so a line runs unbroken beside everything a group holds and
 * stops at its last descendant. An open group also draws its own rail across
 * the hairline beneath it, so the line meets the group's card.
 */
export function treeRails(chain: readonly { depth: number; color: string | undefined }[], own: { depth: number; color: string | undefined } | null): CSSProperties {
  const layers = [
    ...chain.map((at) => ({ ...at, y: '0', height: '100%' })),
    ...(own ? [{ ...own, y: '100%', height: 'var(--tree-fill-gap)' }] : []),
  ];
  if (layers.length === 0) return {};
  const color = (at: { color: string | undefined }) => at.color ?? 'var(--tree-rail-neutral)';
  return {
    '--tree-rail-images': layers.map((at) => `linear-gradient(${color(at)}, ${color(at)})`).join(', '),
    '--tree-rail-positions': layers.map((at) => `calc(var(--tree-indent) * ${at.depth} + var(--tree-rail-offset)) ${at.y}`).join(', '),
    '--tree-rail-sizes': layers.map((at) => `var(--tree-rail-width) ${at.height}`).join(', '),
  } as CSSProperties;
}

/**
 * 🔒 The tree's expand control, one for every tree the application draws: the
 * Nested Tree List's own rows and a List Viewer whose caller nests its rows
 * (decision 8880), so there is one chevron and one tree.
 */
export function TreeToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={styles.treeToggle}
      aria-label={t(expanded ? 'tree.collapse' : 'tree.expand')}
      data-tree-toggle={expanded ? 'open' : 'closed'}
      onClick={(event) => {
        // 🔨 A nested list row may open on a click of its own; the chevron folds and nothing else.
        event.stopPropagation();
        onToggle();
      }}
    >
      <Icon meaning={expanded ? 'collapse' : 'expand'} size="inline" />
    </button>
  );
}

/** 🔒 How many children a row holds, as the tree draws it beside the row's name (piece 4). */
export function TreeChildCount({ count }: { count: number }) {
  return (
    <span className={styles.treeCount} data-child-count={count}>
      {count}
    </span>
  );
}

export function TreeList<N extends TreeNode>({ nodes, label, initiallyExpanded = [], renderPreview, onSelect, selectedId = null, renderMeta, renderActions, onDropOn, renderAfter, fillRows = false, renderLead, isHeading }: TreeListProps<N>) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(initiallyExpanded));
  const [over, setOver] = useState<string | null>(null);
  const draggedId = useRef<string | null>(null);
  const rows = visibleRows(nodes, expanded);
  // One contrast judgement per colour, read from the stylesheet as it stands at render (decision 1290).
  const inks = useMemo(() => {
    const out = new Map<string, FillInk>();
    if (!fillRows) return out;
    for (const node of nodes) if (node.barColor && !out.has(node.barColor)) out.set(node.barColor, fillInkFor(node.barColor));
    return out;
  }, [fillRows, nodes]);
  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  /** The ancestors of a row, top first, each at its depth: what a filled tree draws its rails from. */
  const ancestorsOf = (node: N, depth: number) => {
    const chain: { depth: number; color: string | undefined }[] = [];
    let at = node.parentId ? byId.get(node.parentId) : undefined;
    let level = depth - 1;
    while (at && level >= 0) {
      chain.unshift({ depth: level, color: at.barColor });
      at = at.parentId ? byId.get(at.parentId) : undefined;
      level -= 1;
    }
    return chain;
  };
  const drop = (targetId: string) => {
    const from = draggedId.current;
    draggedId.current = null;
    setOver(null);
    if (!from || !onDropOn) return;
    const move = dropOnNode(nodes, from, targetId);
    if (!move) return;
    const dragged = nodes.find((node) => node.id === move.id);
    const target = nodes.find((node) => node.id === move.parentId);
    if (dragged && target) onDropOn(dragged, target);
  };
  return (
    <ul role="tree" aria-label={label} className={styles.tree} data-tree-list="true" data-tree-fill={fillRows ? 'true' : undefined}>
      {rows.map((row) => {
        const canToggle = !row.isLeaf || !!renderPreview;
        const ink = row.node.barColor ? inks.get(row.node.barColor) ?? null : null;
        const heading = isHeading ? isHeading(row.node) : false;
        const rails = fillRows ? treeRails(ancestorsOf(row.node, row.depth), !row.isLeaf && row.expanded ? { depth: row.depth, color: row.node.barColor } : null) : {};
        const style = { '--tree-depth': row.depth, '--tree-bar': row.node.barColor ?? 'transparent', ...(ink ? { '--tree-ink': FILL_INK_TOKEN[ink] } : {}), ...rails } as CSSProperties;
        const lead = renderLead ? renderLead(row.node) : null;
        const showPreview = row.isLeaf && row.expanded && !!renderPreview;
        return (
          <li
            key={row.node.id}
            role="treeitem"
            aria-level={row.depth + 1}
            aria-expanded={canToggle ? row.expanded : undefined}
            aria-selected={onSelect ? row.node.id === selectedId : undefined}
            className={styles.treeRow}
            style={style}
            data-tree-row={row.node.id}
            data-depth={row.depth}
            data-leaf={row.isLeaf ? 'true' : 'false'}
            data-heading={heading ? 'true' : undefined}
          >
            <div
              className={styles.treeLine}
              draggable={onDropOn ? true : undefined}
              data-drop-target={onDropOn && over === row.node.id ? 'true' : undefined}
              data-fill={ink ?? undefined}
              data-selected={ink && row.node.id === selectedId ? 'true' : undefined}
              onDragStart={onDropOn ? () => { draggedId.current = row.node.id; } : undefined}
              onDragOver={onDropOn ? (e) => { e.preventDefault(); setOver(row.node.id); } : undefined}
              onDragLeave={onDropOn ? () => setOver((current) => (current === row.node.id ? null : current)) : undefined}
              onDrop={onDropOn ? (e) => { e.preventDefault(); drop(row.node.id); } : undefined}
            >
              {ink ? null : <span className={styles.treeBar} aria-hidden="true" />}
              {canToggle ? (
                <TreeToggle expanded={row.expanded} onToggle={() => setExpanded((s) => toggleExpanded(s, row.node.id))} />
              ) : (
                <span className={styles.treeToggle} aria-hidden="true" />
              )}
              {lead ? <span className={styles.treeLead} data-tree-lead={row.node.id}>{lead}</span> : null}
              {onSelect ? (
                <button type="button" className={`${styles.treeLabel} ${styles.treeSelect}`} onClick={() => (heading ? setExpanded((s) => toggleExpanded(s, row.node.id)) : onSelect(row.node))} data-tree-select={row.node.id} data-selected={row.node.id === selectedId ? 'true' : 'false'}>
                  {row.node.label}
                </button>
              ) : (
                <span className={styles.treeLabel}>{row.node.label}</span>
              )}
              {renderMeta ? <span className={styles.treeMeta} data-tree-meta={row.node.id}>{renderMeta(row.node)}</span> : null}
              {!row.isLeaf && !row.expanded && !heading ? (
                <TreeChildCount count={row.childCount} />
              ) : null}
              {renderActions ? <span className={styles.treeActions}>{renderActions(row.node)}</span> : null}
            </div>
            {showPreview ? (
              <div className={styles.treePreview} data-tree-preview="true">
                {renderPreview!(row.node)}
              </div>
            ) : null}
            {renderAfter ? renderAfter(row.node) : null}
          </li>
        );
      })}
    </ul>
  );
}
